import { t } from "i18next";
import z from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  commandUnitSchema,
  LocationSchema,
  nonFutureDateTimeSchema,
  OrganisationRoleSchema,
  shortTextSchema,
  type StaticOptionsList,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";

/**
 * The City of London is a London borough with its own police force, so whether
 * it belongs in a borough list depends on who is doing the recording.
 */
export const cityOfLondonOption: StaticOptionsList[number] = {
  value: "cityOfLondon",
  label: tk("detentionDetails.londonBoroughTookPlace.options.cityOfLondon"),
};

export const londonBoroughOptions: StaticOptionsList = [
  {
    value: "barkingAndDagenham",
    label: tk(
      "detentionDetails.londonBoroughTookPlace.options.barkingAndDagenham",
    ),
  },
  {
    value: "barnet",
    label: tk("detentionDetails.londonBoroughTookPlace.options.barnet"),
  },
  {
    value: "bexley",
    label: tk("detentionDetails.londonBoroughTookPlace.options.bexley"),
  },
  {
    value: "brent",
    label: tk("detentionDetails.londonBoroughTookPlace.options.brent"),
  },
  {
    value: "bromley",
    label: tk("detentionDetails.londonBoroughTookPlace.options.bromley"),
  },
  {
    value: "camden",
    label: tk("detentionDetails.londonBoroughTookPlace.options.camden"),
  },
  {
    value: "cityOfWestminster",
    label: tk(
      "detentionDetails.londonBoroughTookPlace.options.cityOfWestminster",
    ),
  },
  {
    value: "croydon",
    label: tk("detentionDetails.londonBoroughTookPlace.options.croydon"),
  },
  {
    value: "ealing",
    label: tk("detentionDetails.londonBoroughTookPlace.options.ealing"),
  },
  {
    value: "enfield",
    label: tk("detentionDetails.londonBoroughTookPlace.options.enfield"),
  },
  {
    value: "greenwich",
    label: tk("detentionDetails.londonBoroughTookPlace.options.greenwich"),
  },
  {
    value: "hackney",
    label: tk("detentionDetails.londonBoroughTookPlace.options.hackney"),
  },
  {
    value: "hammersmithAndFulham",
    label: tk(
      "detentionDetails.londonBoroughTookPlace.options.hammersmithAndFulham",
    ),
  },
  {
    value: "haringey",
    label: tk("detentionDetails.londonBoroughTookPlace.options.haringey"),
  },
  {
    value: "harrow",
    label: tk("detentionDetails.londonBoroughTookPlace.options.harrow"),
  },
  {
    value: "havering",
    label: tk("detentionDetails.londonBoroughTookPlace.options.havering"),
  },
  {
    value: "hillingdon",
    label: tk("detentionDetails.londonBoroughTookPlace.options.hillingdon"),
  },
  {
    value: "hounslow",
    label: tk("detentionDetails.londonBoroughTookPlace.options.hounslow"),
  },
  {
    value: "islington",
    label: tk("detentionDetails.londonBoroughTookPlace.options.islington"),
  },
  {
    value: "kensingtonAndChelsea",
    label: tk(
      "detentionDetails.londonBoroughTookPlace.options.kensingtonAndChelsea",
    ),
  },
  {
    value: "kingstonUponThames",
    label: tk(
      "detentionDetails.londonBoroughTookPlace.options.kingstonUponThames",
    ),
  },
  {
    value: "lambeth",
    label: tk("detentionDetails.londonBoroughTookPlace.options.lambeth"),
  },
  {
    value: "lewisham",
    label: tk("detentionDetails.londonBoroughTookPlace.options.lewisham"),
  },
  {
    value: "merton",
    label: tk("detentionDetails.londonBoroughTookPlace.options.merton"),
  },
  {
    value: "newham",
    label: tk("detentionDetails.londonBoroughTookPlace.options.newham"),
  },
  {
    value: "redbridge",
    label: tk("detentionDetails.londonBoroughTookPlace.options.redbridge"),
  },
  {
    value: "richmondUponThames",
    label: tk(
      "detentionDetails.londonBoroughTookPlace.options.richmondUponThames",
    ),
  },
  {
    value: "southwark",
    label: tk("detentionDetails.londonBoroughTookPlace.options.southwark"),
  },
  {
    value: "sutton",
    label: tk("detentionDetails.londonBoroughTookPlace.options.sutton"),
  },
  {
    value: "towerHamlets",
    label: tk("detentionDetails.londonBoroughTookPlace.options.towerHamlets"),
  },
  {
    value: "walthamForest",
    label: tk("detentionDetails.londonBoroughTookPlace.options.walthamForest"),
  },
  {
    value: "wandsworth",
    label: tk("detentionDetails.londonBoroughTookPlace.options.wandsworth"),
  },
];

export const londonCustodySuiteOptions: StaticOptionsList = [
  {
    value: "acton",
    label: tk("detentionDetails.confirmCustodySuite.options.acton"),
  },
  {
    value: "barkingAndDagenhamFreshwharf",
    label: tk(
      "detentionDetails.confirmCustodySuite.options.barkingAndDagenhamFreshwharf",
    ),
  },
  {
    value: "bexleyheath",
    label: tk("detentionDetails.confirmCustodySuite.options.bexleyheath"),
  },
  {
    value: "bethnalGreen",
    label: tk("detentionDetails.confirmCustodySuite.options.bethnalGreen"),
  },
  {
    value: "brixton",
    label: tk("detentionDetails.confirmCustodySuite.options.brixton"),
  },
  {
    value: "bromleyPfi",
    label: tk("detentionDetails.confirmCustodySuite.options.bromleyPfi"),
  },
  {
    value: "colindale",
    label: tk("detentionDetails.confirmCustodySuite.options.colindale"),
  },
  {
    value: "cxFirstFloor",
    label: tk("detentionDetails.confirmCustodySuite.options.cxFirstFloor"),
  },
  {
    value: "cxGroundFloor",
    label: tk("detentionDetails.confirmCustodySuite.options.cxGroundFloor"),
  },
  {
    value: "croydon",
    label: tk("detentionDetails.confirmCustodySuite.options.croydon"),
  },
  {
    value: "edmonton",
    label: tk("detentionDetails.confirmCustodySuite.options.edmonton"),
  },
  {
    value: "forestGate",
    label: tk("detentionDetails.confirmCustodySuite.options.forestGate"),
  },
  {
    value: "harrow",
    label: tk("detentionDetails.confirmCustodySuite.options.harrow"),
  },
  {
    value: "hammersmith",
    label: tk("detentionDetails.confirmCustodySuite.options.hammersmith"),
  },
  {
    value: "heathrow",
    label: tk("detentionDetails.confirmCustodySuite.options.heathrow"),
  },
  {
    value: "holborn",
    label: tk("detentionDetails.confirmCustodySuite.options.holborn"),
  },
  {
    value: "hounslow",
    label: tk("detentionDetails.confirmCustodySuite.options.hounslow"),
  },
  {
    value: "ilford",
    label: tk("detentionDetails.confirmCustodySuite.options.ilford"),
  },
  {
    value: "islington",
    label: tk("detentionDetails.confirmCustodySuite.options.islington"),
  },
  {
    value: "kingston",
    label: tk("detentionDetails.confirmCustodySuite.options.kingston"),
  },
  {
    value: "kentishTown",
    label: tk("detentionDetails.confirmCustodySuite.options.kentishTown"),
  },
  {
    value: "leyton",
    label: tk("detentionDetails.confirmCustodySuite.options.leyton"),
  },
  {
    value: "lewishamPfi",
    label: tk("detentionDetails.confirmCustodySuite.options.lewishamPfi"),
  },
  {
    value: "peckham",
    label: tk("detentionDetails.confirmCustodySuite.options.peckham"),
  },
  {
    value: "plumstead",
    label: tk("detentionDetails.confirmCustodySuite.options.plumstead"),
  },
  {
    value: "romford",
    label: tk("detentionDetails.confirmCustodySuite.options.romford"),
  },
  {
    value: "stokeNewington",
    label: tk("detentionDetails.confirmCustodySuite.options.stokeNewington"),
  },
  {
    value: "suttonPfi",
    label: tk("detentionDetails.confirmCustodySuite.options.suttonPfi"),
  },
  {
    value: "wandsworth",
    label: tk("detentionDetails.confirmCustodySuite.options.wandsworth"),
  },
  {
    value: "walworth",
    label: tk("detentionDetails.confirmCustodySuite.options.walworth"),
  },
  {
    value: "wembley",
    label: tk("detentionDetails.confirmCustodySuite.options.wembley"),
  },
  {
    value: "woodGreen",
    label: tk("detentionDetails.confirmCustodySuite.options.woodGreen"),
  },
];

export const locationTypeOptions: StaticOptionsList = [
  {
    value: "homeAddress",
    label: tk("mcaIncidentDetails.locationType.options.homeAddress"),
  },
  {
    value: "railwayLines",
    label: tk("mcaIncidentDetails.locationType.options.railwayLines"),
  },
  {
    value: "hospitalWards",
    label: tk("mcaIncidentDetails.locationType.options.hospitalWards"),
  },
  {
    value: "placeOfHeight",
    label: tk("mcaIncidentDetails.locationType.options.placeOfHeight"),
  },
  {
    value: "policeStationsCustody",
    label: tk("mcaIncidentDetails.locationType.options.policeStationsCustody"),
  },
  {
    value: "offices",
    label: tk("mcaIncidentDetails.locationType.options.offices"),
  },
  {
    value: "school",
    label: tk("mcaIncidentDetails.locationType.options.school"),
  },
  {
    value: "street",
    label: tk("mcaIncidentDetails.locationType.options.street"),
  },
  {
    value: "park",
    label: tk("mcaIncidentDetails.locationType.options.park"),
  },
  {
    value: "hotel",
    label: tk("mcaIncidentDetails.locationType.options.hotel"),
  },
  {
    value: "nonResidentialPartsRestrictedEntry",
    label: tk(
      "mcaIncidentDetails.locationType.options.nonResidentialPartsRestrictedEntry",
    ),
  },
  {
    value: "emergencyDepartment",
    label: tk("mcaIncidentDetails.locationType.options.emergencyDepartment"),
  },
  {
    value: "physicalHealthInpatientWard",
    label: tk(
      "mcaIncidentDetails.locationType.options.physicalHealthInpatientWard",
    ),
  },
  {
    value: "bridgeOverWater",
    label: tk("mcaIncidentDetails.locationType.options.bridgeOverWater"),
  },
  {
    value: "bridgeOverRoad",
    label: tk("mcaIncidentDetails.locationType.options.bridgeOverRoad"),
  },
  {
    value: "noneOfTheAbove",
    label: tk("mcaIncidentDetails.locationType.options.noneOfTheAbove"),
  },
];

/** The locations offered to a force whose ground is the rail network. */
export const railwayLocationTypeOptions: StaticOptionsList = [
  {
    value: "homeAddress",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.homeAddress"),
  },
  {
    value: "policeStationsCustody",
    label: tk(
      "mcaIncidentDetails.locationType.railwayOptions.policeStationsCustody",
    ),
  },
  {
    value: "onTrain",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.onTrain"),
  },
  {
    value: "concourse",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.concourse"),
  },
  {
    value: "platform",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.platform"),
  },
  {
    value: "stationToilet",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.stationToilet"),
  },
  {
    value: "stationForecourt",
    label: tk(
      "mcaIncidentDetails.locationType.railwayOptions.stationForecourt",
    ),
  },
  {
    value: "carPark",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.carPark"),
  },
  {
    value: "onTracksAtStation",
    label: tk(
      "mcaIncidentDetails.locationType.railwayOptions.onTracksAtStation",
    ),
  },
  {
    value: "onTracksAwayFromStation",
    label: tk(
      "mcaIncidentDetails.locationType.railwayOptions.onTracksAwayFromStation",
    ),
  },
  {
    value: "lineside",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.lineside"),
  },
  {
    value: "bridge",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.bridge"),
  },
  {
    value: "levelCrossing",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.levelCrossing"),
  },
  {
    value: "disusedRailwayOrStation",
    label: tk(
      "mcaIncidentDetails.locationType.railwayOptions.disusedRailwayOrStation",
    ),
  },
  {
    value: "tunnel",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.tunnel"),
  },
  {
    value: "depot",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.depot"),
  },
  {
    value: "otherRailwayInfrastructure",
    label: tk(
      "mcaIncidentDetails.locationType.railwayOptions.otherRailwayInfrastructure",
    ),
  },
  {
    value: "offBtpJurisdiction",
    label: tk(
      "mcaIncidentDetails.locationType.railwayOptions.offBtpJurisdiction",
    ),
  },
  {
    value: "btpOffice",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.btpOffice"),
  },
  {
    value: "ticketBarriers",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.ticketBarriers"),
  },
  {
    value: "hotelWithinRailwayEnvironment",
    label: tk(
      "mcaIncidentDetails.locationType.railwayOptions.hotelWithinRailwayEnvironment",
    ),
  },
  {
    value: "publicHouse",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.publicHouse"),
  },
  {
    // `noneOfTheAbove` is the shared value for a location none of the offered
    // ones describes; this list simply names it as the officer would say it.
    value: "noneOfTheAbove",
    label: tk("mcaIncidentDetails.locationType.railwayOptions.other"),
  },
];

/**
 * The questions every MCA incident details form asks, with no pages allocated:
 * page order belongs to the workflow that composes these.
 *
 * A field added here must be placed on a page by every caller: `build()` throws
 * when a field has no page, and these forms are built at module scope, so the
 * failure lands on import, naming the field.
 *
 * Each call returns a fresh builder, so one caller's form cannot leak into
 * another's.
 */
export const buildMcaIncidentDetailsSharedFields = (args?: {
  locationTypeOptions?: StaticOptionsList;
}) =>
  new MultiPageFormBuilder()
    .withFieldAlwaysVisible({
      fieldName: "dateTimeOfIncident",
      type: "date-time",
      timeLabel: tk("mcaIncidentDetails.timeOfIncident"),
      dateLabel: tk("mcaIncidentDetails.dateOfIncident"),
      requiredLabel: tk("mcaIncidentDetails.dateTimeOfIncidentRequired"),
      disableFuture: true,
      ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    })
    .withFieldAlwaysVisible({
      fieldName: "incidentLocation",
      type: "location",
      ...new FieldBuilder().withSchema(LocationSchema).build(),
      label: tk("mcaIncidentDetails.incidentLocation"),
      requiredLabel: tk("mcaIncidentDetails.incidentLocationRequired"),
    })
    .withFieldAlwaysVisible({
      fieldName: "bcuIncidentTookPlace",
      type: "organisation",
      filterByRoles: [OrganisationRoleSchema.enum.PoliceCommandUnit],
      ...new FieldBuilder()
        .withSchema(commandUnitSchema)
        .captureValueLabel()
        .build(),
      label: tk("mcaIncidentDetails.bcuIncidentTookPlace"),
      requiredLabel: tk("mcaIncidentDetails.bcuIncidentTookPlaceRequired"),
    })
    .withFieldAlwaysVisible({
      /**
       * The location values are shared so that an incident recorded by one
       * force stays comparable with one recorded by another. Which of them a
       * workflow offers is its own decision, made by the options it passes.
       */
      fieldName: "locationType",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "homeAddress" as const,
            "railwayLines" as const,
            "hospitalWards" as const,
            "placeOfHeight" as const,
            "policeStationsCustody" as const,
            "offices" as const,
            "school" as const,
            "street" as const,
            "park" as const,
            "hotel" as const,
            "nonResidentialPartsRestrictedEntry" as const,
            "emergencyDepartment" as const,
            "physicalHealthInpatientWard" as const,
            "bridgeOverWater" as const,
            "bridgeOverRoad" as const,
            "onTrain" as const,
            "concourse" as const,
            "platform" as const,
            "stationToilet" as const,
            "stationForecourt" as const,
            "carPark" as const,
            "onTracksAtStation" as const,
            "onTracksAwayFromStation" as const,
            "lineside" as const,
            "bridge" as const,
            "levelCrossing" as const,
            "disusedRailwayOrStation" as const,
            "tunnel" as const,
            "depot" as const,
            "otherRailwayInfrastructure" as const,
            "offBtpJurisdiction" as const,
            "btpOffice" as const,
            "ticketBarriers" as const,
            "hotelWithinRailwayEnvironment" as const,
            "publicHouse" as const,
            "noneOfTheAbove" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      label: tk("mcaIncidentDetails.locationType.title"),
      requiredLabel: tk("mcaIncidentDetails.locationType.required"),
      options: args?.locationTypeOptions ?? locationTypeOptions,
    })
    .withFieldConditionallyVisible({
      fieldName: "custodyNumber",
      type: "textbox",
      ...new FieldBuilder().withSchema(shortTextSchema).build(),
      label: tk("detentionDetails.custodyNumber_mca"),
      requiredLabel: tk("detentionDetails.custodyNumberRequired"),
      isVisible: ({ formValues }) =>
        formValues.locationType?.value === "policeStationsCustody",
    })
    .withReadonlyFieldConditionallyVisible({
      fieldName: "custodyNumberDescriptionBanner",
      type: "banner",
      bannerType: "info",
      body: tk("detentionDetails.custodyNumberDescriptionBanner"),
      isVisible: ({ formValues }) =>
        formValues.locationType?.value === "policeStationsCustody",
    });

/**
 * The custody suite the person was held in, as one of a fixed list of London
 * suites. Only meaningful where every suite reachable by the recording force is
 * on that list.
 */
export const withLondonCustodySuiteField = (
  builder: ReturnType<typeof buildMcaIncidentDetailsSharedFields>,
) =>
  builder.withFieldConditionallyVisible({
    fieldName: "confirmCustodySuite",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "acton" as const,
          "barkingAndDagenhamFreshwharf" as const,
          "bethnalGreen" as const,
          "bexleyheath" as const,
          "brixton" as const,
          "bromleyPfi" as const,
          "colindale" as const,
          "croydon" as const,
          "cxFirstFloor" as const,
          "cxGroundFloor" as const,
          "edmonton" as const,
          "forestGate" as const,
          "hammersmith" as const,
          "harrow" as const,
          "heathrow" as const,
          "holborn" as const,
          "hounslow" as const,
          "ilford" as const,
          "islington" as const,
          "kentishTown" as const,
          "kingston" as const,
          "lewishamPfi" as const,
          "leyton" as const,
          "peckham" as const,
          "plumstead" as const,
          "romford" as const,
          "stokeNewington" as const,
          "suttonPfi" as const,
          "walworth" as const,
          "wandsworth" as const,
          "wembley" as const,
          "woodGreen" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.confirmCustodySuite.title"),
    requiredLabel: tk("detentionDetails.confirmCustodySuite.required"),
    options: londonCustodySuiteOptions,
    isVisible: ({ formValues }) => {
      return formValues.custodyNumber?.value !== undefined;
    },
  });

/**
 * Which London borough the incident happened in. Only meaningful where every
 * location the recording force covers is inside London.
 *
 * Takes the builder the custody suite group returns rather than the shared one,
 * so that composing both groups keeps both sets of fields in the type. Each
 * group's parameter type fixes what its caller may pass, so two groups can only
 * be chained in one direction.
 */
export const withLondonBoroughIncidentField = (
  builder: ReturnType<typeof withLondonCustodySuiteField>,
  args?: { londonBoroughOptions?: StaticOptionsList },
) =>
  builder.withFieldAlwaysVisible({
    fieldName: "londonBoroughIncidentTookPlace",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "barkingAndDagenham" as const,
          "barnet" as const,
          "bexley" as const,
          "brent" as const,
          "bromley" as const,
          "camden" as const,
          "cityOfLondon" as const,
          "cityOfWestminster" as const,
          "croydon" as const,
          "ealing" as const,
          "enfield" as const,
          "greenwich" as const,
          "hackney" as const,
          "hammersmithAndFulham" as const,
          "haringey" as const,
          "harrow" as const,
          "havering" as const,
          "hillingdon" as const,
          "hounslow" as const,
          "islington" as const,
          "kensingtonAndChelsea" as const,
          "kingstonUponThames" as const,
          "lambeth" as const,
          "lewisham" as const,
          "merton" as const,
          "newham" as const,
          "redbridge" as const,
          "richmondUponThames" as const,
          "southwark" as const,
          "sutton" as const,
          "towerHamlets" as const,
          "walthamForest" as const,
          "wandsworth" as const,
        ] as const),
      )
      .captureValueLabel()
      .build(),
    label: tk("detentionDetails.londonBoroughTookPlace.incident.title"),
    requiredLabel: tk(
      "detentionDetails.londonBoroughTookPlace.incident.required",
    ),
    options: args?.londonBoroughOptions ?? londonBoroughOptions,
  });

/**
 * The shared questions plus both London questions, in the order they are asked.
 */
export const buildMcaIncidentDetailsFormSpecification = (args?: {
  londonBoroughOptions?: StaticOptionsList;
}) =>
  withLondonBoroughIncidentField(
    withLondonCustodySuiteField(buildMcaIncidentDetailsSharedFields()),
    args,
  )
    .withPage(["dateTimeOfIncident"])
    .withPage(["incidentLocation"])
    .withPage(["bcuIncidentTookPlace"])
    .withPage(["londonBoroughIncidentTookPlace"])
    .withPage(["locationType"])
    .withPage([
      "custodyNumber",
      "custodyNumberDescriptionBanner",
      "confirmCustodySuite",
    ])
    .build({ t });

export const formSpecification = buildMcaIncidentDetailsFormSpecification();

export type McaIncidentDetailsFormData = ReturnType<
  typeof formSpecification
>["formType"];
